// Defines the structure and parameters for Lifestyle data entry and display.

// Define a standard 5-gradient Likert scale for health-related questions (Kept for reference, but not used for Movement activity days anymore)
export const healthLikertScaleOptions = [
  { value: 1, label: '1 - Worse for health' },
  { value: 2, label: '2 - Poor for health' },
  { value: 3, label: '3 - Neutral/Average' },
  { value: 4, label: '4 - Good for health' },
  { value: 5, label: '5 - Best for health' },
];

// Define options for activity days (0-7 days scale)
export const activityDaysOptions = [
  { value: 'never', label: 'Never (0 days)' },
  { value: 'rarely', label: 'Rarely (1-2 days)' },
  { value: 'sometimes', label: 'Sometimes (3-4 days)' },
  { value: 'often', label: 'Often (5-6 days)' },
  { value: 'always', label: 'Always (7 days)' },
];

// Define options for specific questions with predefined answers
export const incidentalWalkingTimeOptions = [
  { value: '>60', label: '60 minutes or more' },
  { value: '45-59', label: '45-59 minutes' },
  { value: '30-44', label: '30-44 minutes' },
  { value: '15-29', label: '15-29 minutes' },
  { value: '<15', label: 'Less than 15 minutes' },
];

export const sedentaryHoursOptions = [
  { value: '<4', label: 'Less than 4 hours' },
  { value: '4-5', label: '4-5 hours' },
  { value: '6-7', label: '6-7 hours' },
  { value: '8-9', label: '8-9 hours' },
  { value: '>10', label: '10 hours or more' },
];

export const sittingBreakDaysOptions = [
  { value: 'always', label: 'Always – I get up every hour without fail' },
  { value: 'often', label: 'Often – I stand or move around about 4-5 times a day' },
  { value: 'sometimes', label: 'Sometimes – I stand or move around about 2-3 times a day' },
  { value: 'rarely', label: 'Rarely – I stand or move around once a day' },
  { value: 'never', label: 'Never – I stay seated without breaks' },
];

// Define options for Red-flag symptoms (used in form checkboxes)
export const redFlagSymptomOptions = [
    { key: 'red_flag_sob', label: 'Shortness of breath beyond normal exertion' },
    { key: 'red_flag_chest_pain', label: 'Chest pain, tightness, or pressure' },
    { key: 'red_flag_palpitations', label: 'Fast or irregular heartbeats (palpitations)' },
    { key: 'red_flag_dizziness', label: 'Dizziness, lightheadedness, or fainting' },
    { key: 'red_flag_pain', label: 'Joint or muscle pain that stops you from continuing' },
    { key: 'red_flag_imbalance', label: 'Loss of balance or falls' },
];

// Define options for Appetite (used in form Select)
export const appetiteOptions = [
  { value: 'very_good', label: 'Very good – I feel hungry for most meals.' },
  { value: 'good', label: 'Good – I usually enjoy my food.' },
  { value: 'fair', label: 'Fair – Some days I’m less interested in eating.' },
  { value: 'poor', label: 'Poor – I often have little desire to eat.' },
  { value: 'very_poor', label: 'Very poor – I rarely feel like eating at all.' },
];


export const lifestyleCategories = [
  {
    key: 'nutrition',
    title: 'Nutrition',
    parameters: [
      // Original Nutrition parameters
      { key: 'protein_intake', name: 'Protein intake', questionText: 'In a typical week, which type of protein do you eat most often?', type: 'Questionnaire', options: [ { value: 'plant', label: 'Mostly plant-based' }, { value: 'fish', label: 'Mostly fish or seafood' }, { value: 'mix', label: 'Mix of fish, poultry, and plant' }, { value: 'red_meat', label: 'Mostly red meats (beef, pork, goat)' }, { value: 'processed_meat', label: 'Mostly processed meats (sausage, Chinese sausage, canned meat, Vietnamese pork sausage)' } ], is_core_component: true, clinicalInterpretation: 'Type of protein consumed impacts overall health and chronic disease risk.' },
      { key: 'fruit_intake', name: 'Fruit intake', questionText: 'On a typical day, how many pieces of fruits do you eat?', type: 'Questionnaire', options: [ { value: '4+', label: '4 pieces or more' }, { value: '3', label: '3 pieces' }, { value: '2', label: '2 pieces' }, { value: '1', label: '1 piece' }, { value: '0', label: '0 pieces' } ], is_core_component: true, clinicalInterpretation: 'Fruits are rich in vitamins, minerals, and fiber. Aim for at least 2-3 servings daily.' },
      { key: 'vegetables_intake', name: 'Vegetables intake', questionText: 'On a typical day, how many handfuls of vegetables do you eat?', type: 'Questionnaire', options: [ { value: '4+', label: '4 or more handfuls' }, { value: '3', label: '3 handfuls' }, { value: '2', label: '2 handfuls' }, { value: '1', label: '1 handful' }, { value: '0', label: '0 handfuls' } ], is_core_component: true, clinicalInterpretation: 'Vegetables provide essential nutrients and antioxidants. Aim for at least 3-5 servings daily.' },
      { key: 'refined_carbs_intake', name: 'Refined carbs intake', questionText: 'In a typical week, how often do you eat refined starchy foods?', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'Frequent consumption of refined carbohydrates can negatively impact blood sugar and weight.' },
      { key: 'fiber_intake', name: 'Fiber intake', questionText: 'In a typical week, how many times do you eat legumes?', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'Dietary fiber is important for digestive health and blood sugar control. Legumes are a good source.' },
      { key: 'seeds_nuts_intake', name: 'Seeds & nuts intake', questionText: 'In a typical week, how often do you eat seeds or nuts?', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'Seeds and nuts provide healthy fats, fiber, and micronutrients.' },
      { key: 'sodium_intake', name: 'Sodium intake', questionText: 'In a typical week, how often do you add salt, fish sauce, soy sauce, to your food?', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'High sodium intake is linked to high blood pressure. Be mindful of added salt.' },
      { key: 'sugar_intake', name: 'Sugar intake', questionText: 'On a typical day, how many sugary foods or drinks do you have?', type: 'Questionnaire', options: [ { value: '0', label: '0 items' }, { value: '1', label: '1 item' }, { value: '2', label: '2 items' }, { value: '3', label: '3 items' }, { value: '4+', label: '4 or more items' } ], is_core_component: true, clinicalInterpretation: 'Excessive sugar intake contributes to various health problems. Limit sugary foods and drinks.' },
      { key: 'fried_food_intake', name: 'Fried food intake', questionText: 'In a typical week, how often do you eat fried food?', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'Fried foods are often high in unhealthy fats and calories.' },
      { key: 'healthy_oil_use', name: 'Healthy-oil use', questionText: 'In a typical week, how often do you cook with plant-based oils instead of animal fats?', type: 'Questionnaire', options: [ { value: 'always', label: 'Always (every time)' }, { value: 'often', label: 'Often (most days)' }, { value: 'sometimes', label: 'Sometimes (a few days)' }, { value: 'rarely', label: 'Rarely (once a week)' }, { value: 'never', label: 'Never' } ], is_core_component: true, clinicalInterpretation: 'Choosing plant-based oils over animal fats can improve heart health.' },
      { key: 'ultraprocessed_intake', name: 'Ultraprocessed food', questionText: 'In a typical week, how often do you eat fast-food or processed food?', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'Ultra-processed foods are often low in nutrients and high in unhealthy additives.' },
      { key: 'water_intake', name: 'Water intake', questionText: 'On a typical day, how many glasses of water or unsweetened drinks do you have?', type: 'Questionnaire', options: [ { value: '10+', label: '10 or more glasses' }, { value: '7-9', label: '7-9 glasses' }, { value: '5-6', label: '5-6 glasses' }, { value: '3-4', label: '3-4 glasses' }, { value: '0-2', label: '2 or fewer glasses' } ], is_core_component: true, clinicalInterpretation: 'Adequate hydration is crucial for overall health and bodily functions.' },
      { key: 'alcohol_intake', name: 'Alcohol', questionText: 'In a typical week, how many standard alcoholic drinks do you have?', type: 'Questionnaire', options: [ { value: '0', label: '0 drinks' }, { value: '1-3', label: '1-3 drinks' }, { value: '4-7', label: '4-7 drinks' }, { value: '8-14', label: '8-14 drinks' }, { value: '14+', label: 'More than 14 drinks' } ], is_core_component: true, clinicalInterpretation: 'Excessive alcohol consumption has numerous health risks. Moderation is key.' },

      // Nutrition Status Header (for form display)
      { key: 'nutrition_status_header', name: 'Nutrition status', questionText: 'Select all of the following that apply to you over the past 3 months:', type: 'Header', is_core_component: false, clinicalInterpretation: '' },
      // Nutrition Status items (BooleanCheckbox for table/form)
      { key: 'weight_loss_concern', name: 'Nutrition status - Weight loss (concern)', questionText: "I've lost weight without trying and it concerns me", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Unexplained weight loss can be a sign of underlying health issues.' },
      { key: 'weight_gain_concern', name: 'Nutrition status - Weight gain (concern)', questionText: "I've gained weight without trying and it concerns me", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Unexplained weight gain can also indicate health concerns or lifestyle imbalances.' },
      { key: 'appetite_lower_normal', name: 'Nutrition status - Eating less', questionText: "My appetite has been much lower than normal", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Significant changes in appetite should be evaluated.' },
      { key: 'appetite_higher_normal', name: 'Nutrition status - Eating more', questionText: "My appetite has been much higher than normal", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Significant changes in appetite should be evaluated.' },
      { key: 'skip_meals_hungry', name: 'Nutrition status - Skip meals', questionText: "I sometimes skip meals because I don't feel hungry", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Regularly skipping meals can affect energy levels and nutrient intake.' },
      { key: 'full_small_amount', name: 'Nutrition status - Early satiety', questionText: "I often feel full after eating only a small amount", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Feeling full quickly (early satiety) can sometimes indicate digestive issues.' },
      { key: 'difficulty_chewing_swallowing', name: 'Nutrition status - Chew and swallow', questionText: "I have difficulty chewing or swallowing food", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Difficulties with chewing or swallowing can impact nutrition and may need medical attention.' },
      { key: 'problems_taste_smell', name: 'Nutrition status - Taste and smell', questionText: "I have problems with my taste and smell", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Changes in taste or smell can affect appetite and enjoyment of food.' },
      { key: 'worry_food_access', name: 'Nutrition status - Food access', questionText: "I worry that I don't have enough money or access to buy balanced meals", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Food insecurity can significantly impact health and well-being.' },
      
      // Eating Behaviors Header (for form display)
      { key: 'eating_behaviors_header', name: 'Eating behaviors', questionText: 'Think about the past wee, select all of the following that describe your usual eating behaviors:', type: 'Header', is_core_component: false, clinicalInterpretation: '' },
      // Eating Behaviors items (BooleanCheckbox for table/form)
      { key: 'bored_eating', name: 'Eating behavior - Bored eating', questionText: "I eat even when I'm not physically hungry (stress, boredom).", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Eating due to emotions rather than hunger can lead to overconsumption.' },
      { key: 'busy_eating', name: 'Eating behavior - Busy eating', questionText: "I skip meals because I'm too busy.", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Skipping meals due to busyness can disrupt metabolism and energy.' },
      { key: 'late_eating', name: 'Eating behavior - Late eating', questionText: "I often eat late at night (after 9 PM).", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Eating large meals late at night can interfere with sleep and digestion.' },
      { key: 'binge_eating', name: 'Eating behavior - Binge eating', questionText: "I binge-eat large amounts of food at once.", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Binge eating can be a sign of disordered eating patterns.' },
      { key: 'guilty_eating', name: 'Eating behavior - Guilty eating', questionText: "I feel guilty or anxious after eating.", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Feelings of guilt or anxiety around food can indicate an unhealthy relationship with eating.' },
      { key: 'rushed_eating', name: 'Eating behavior - Rushed eating', questionText: "I eat very quickly or in a rush.", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Eating too quickly can lead to overeating and poor digestion.' },
      { key: 'distracted_eating', name: 'Eating behavior - Distracted eating', questionText: "I frequently eat in front of the TV, computer, or phone without paying attention.", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Distracted eating can lead to overconsumption as the body may not register fullness cues.' },
      { key: 'fad_diets', name: 'Eating behavior - Fad diets', questionText: "I follow strict or fad diets without professional guidance.", type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Fad diets are often unsustainable and may lack essential nutrients.' },
    ],
  },
  {
    key: 'movement',
    title: 'Movement',
    parameters: [
      // Movement questions from image
      { key: 'moderate_activity_days', name: 'Moderate-activity days', questionText: 'In a typical week, on how many days do you spend at least 30 minutes doing activities that make you breathe a little faster but you can still talk', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'Regular moderate activity is key for cardiovascular health and overall well-being.' },
      { key: 'vigorous_activity_days', name: 'Vigorous-activity days', questionText: 'In a typical week, how many days do you do harder activities that leave you breathing heavily so you can only speak a few words at a time', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'Vigorous activity provides additional cardiovascular benefits and improves fitness.' },
      { key: 'strength_training_days', name: 'Strength-training days', questionText: 'How many days a week do you do exercises that work your muscles—like push-ups, resistance bands, weight machines?', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'Strength training is important for muscle mass, bone density, and metabolism.' },
      { key: 'flexibility_stretching_days', name: 'Flexibility/stretching days', questionText: 'How many days a week do you spend at least 10 minutes on stretching, yoga, or other flexibility exercises?', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'Flexibility exercises help maintain range of motion and prevent injuries.' },
      { key: 'balance_practice_days', name: 'Balance-practice days', questionText: 'How many days a week do you practice balance activities—such as tai chi, standing on one leg, or heel-to-toe walking?', type: 'Questionnaire', options: activityDaysOptions, is_core_component: true, clinicalInterpretation: 'Balance exercises are important for stability and fall prevention, especially as we age.' },
      { key: 'incidental_walking_time', name: 'Incidental-walking time', questionText: 'On a typical day, about how many minutes do you spend walking in total?', type: 'Questionnaire', options: incidentalWalkingTimeOptions, is_core_component: true, clinicalInterpretation: 'Incidental activity throughout the day contributes to overall energy expenditure and health.' },
      { key: 'sedentary_hours', name: 'Sedentary hours', questionText: 'On a typical day, about how many hours do you spend sitting', type: 'Questionnaire', options: sedentaryHoursOptions, is_core_component: true, clinicalInterpretation: 'Prolonged sitting is a risk factor for various chronic diseases. Aim to minimize sedentary time.' },
      { key: 'sitting_break_days', name: 'Sitting-break days', questionText: 'On a typical day, how often do you break up long periods of sitting by standing up or moving around', type: 'Questionnaire', options: sittingBreakDaysOptions, is_core_component: true, clinicalInterpretation: 'Regularly breaking up sitting time with movement is beneficial for health.' },
      // Red-flag symptoms header (for form display only)
      { key: 'red_flag_symptoms_header', name: 'Red-flag symptoms', questionText: 'In the past week, have you experienced any of these when you move or exercise? (select all that apply)', type: 'Header', is_core_component: false, clinicalInterpretation: '' }, 
      // Individual Red-flag symptoms (for table display and form checkboxes) - Updated names
      { key: 'red_flag_sob', name: 'Symptoms - Dyspnea', questionText: 'Shortness of breath beyond normal exertion', type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Unusual shortness of breath during activity may indicate heart or lung issues.' },
      { key: 'red_flag_chest_pain', name: 'Symptoms - Chest discomfort', questionText: 'Chest pain, tightness, or pressure', type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Chest discomfort during activity should always be evaluated by a doctor.' },
      { key: 'red_flag_palpitations', name: 'Symptoms - Palpitation', questionText: 'Fast or irregular heartbeats (palpitations)', type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Palpitations during exercise can sometimes signal an underlying heart condition.' },
      { key: 'red_flag_dizziness', name: 'Symptoms - Dizziness', questionText: 'Dizziness, lightheadedness, or fainting', type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Dizziness or fainting during activity requires medical attention.' },
      { key: 'red_flag_pain', name: 'Symptoms - Pain', questionText: 'Joint or muscle pain that stops you from continuing', type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Pain that forces you to stop activity should be assessed to prevent injury.' },
      { key: 'red_flag_imbalance', name: 'Symptoms - Imbalance', questionText: 'Loss of balance or falls', type: 'BooleanCheckbox', is_core_component: true, clinicalInterpretation: 'Loss of balance or falls during activity can indicate neurological or musculoskeletal issues.' },
    ],
  },
  {
    key: 'recovery',
    title: 'Recovery',
    parameters: [
      {
        key: 'sleep_quality',
        name: 'Sleep quality',
        questionText: 'Over the past week, how often did you wake up feeling refreshed and rested?',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Not at all' },
          { value: 2, label: '2 - Rarely' },
          { value: 3, label: '3 - Sometimes' },
          { value: 4, label: '4 - Often' },
          { value: 5, label: '5 - Always' },
        ],
        is_core_component: true, clinicalInterpretation: 'Waking up refreshed is a key indicator of good sleep quality.'
      },
      {
        key: 'sleep_duration',
        name: 'Sleep duration',
        questionText: 'On a typical night, how many hours of sleep do you get?',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Less than 5 hours' },
          { value: 2, label: '2 - 5-6 hours' },
          { value: 3, label: '3 - 7-8 hours' },
          { value: 4, label: '4 - 9-10 hours' },
          { value: 5, label: '5 - More than 10 hours' },
        ],
        is_core_component: true, clinicalInterpretation: 'Most adults need 7-9 hours of sleep per night for optimal health.'
      },
      {
        key: 'sleep_continuity',
        name: 'Sleep continuity',
        questionText: 'In a typical week, how many nights did you wake up two or more times?',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Always (7 days)' },
          { value: 2, label: '2 - Often (5-6 days)' },
          { value: 3, label: '3 - Sometimes (3-4 days)' },
          { value: 4, label: '4 - Rarely (1-2 days)' },
          { value: 5, label: '5 - Never (0 days)' },
        ],
        is_core_component: true, clinicalInterpretation: 'Frequent awakenings can disrupt sleep cycles and reduce sleep quality.'
      },
      {
        key: 'sleep_onset',
        name: 'Sleep onset',
        questionText: 'How often does it take you longer than 30 minutes to fall asleep?',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Always (7 days)' },
          { value: 2, label: '2 - Often (5-6 days)' },
          { value: 3, label: '3 - Sometimes (3-4 days)' },
          { value: 4, label: '4 - Rarely (1-2 days)' },
          { value: 5, label: '5 - Never (0 days)' },
        ],
        is_core_component: true, clinicalInterpretation: 'Regularly taking a long time to fall asleep can be a sign of insomnia.'
      },
      {
        key: 'bedtime_consistency',
        name: 'Bedtime consistency',
        questionText: 'How many days per week do you go to bed within the same 30-minute window?',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Never (0 days)' },
          { value: 2, label: '2 - Rarely (1-2 days)' },
          { value: 3, label: '3 - Sometimes (3-4 days)' },
          { value: 4, label: '4 - Often (5-6 days)' },
          { value: 5, label: '5 - Always (7 days)' },
        ],
        is_core_component: true, clinicalInterpretation: 'A consistent sleep schedule helps regulate your body\'s internal clock.'
      },
      {
        key: 'late_bedtime',
        name: 'Late bedtime',
        questionText: 'How many nights a week do you go to sleep after 11 PM?',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Always (7 nights)' },
          { value: 2, label: '2 - Often (5-6 nights)' },
          { value: 3, label: '3 - Sometimes (3-4 nights)' },
          { value: 4, label: '4 - Rarely (1-2 nights)' },
          { value: 5, label: '5 - Never (0 nights)' },
        ],
        is_core_component: true, clinicalInterpretation: 'Consistently late bedtimes can disrupt circadian rhythms and sleep quality.'
      },
      {
        key: 'sleep_environment_factors',
        name: 'Sleep environment',
        questionText: 'In the past week, which of these environmental factors disturbed your sleep?',
        type: 'BooleanCheckbox',
        options: [
          { key: 'uncomfortable_mattress', label: 'Bedding', is_core_component: true, clinicalInterpretation: 'Comfortable bedding is important for good sleep.' },
          { key: 'room_temperature', label: 'Temperature', is_core_component: true, clinicalInterpretation: 'A cool, comfortable room temperature promotes better sleep.' },
          { key: 'bright_light', label: 'Bright light', is_core_component: true, clinicalInterpretation: 'Exposure to bright light before bed can interfere with sleep.' },
          { key: 'loud_noises', label: 'Loud noises', is_core_component: true, clinicalInterpretation: 'A quiet environment is conducive to restful sleep.' },
          { key: 'electronic_screens', label: 'Blue light', is_core_component: true, clinicalInterpretation: 'Blue light from screens can suppress melatonin and disrupt sleep.' },
          { key: 'caffeine_stimulants', label: 'Caffeine', is_core_component: true, clinicalInterpretation: 'Caffeine or other stimulants close to bedtime can make it hard to fall asleep.' },
        ],
        is_core_component: false, clinicalInterpretation: 'Various environmental factors can impact sleep quality.' // Parent item itself is not a direct core scoring component
      },
      {
        key: 'napping_frequency',
        name: 'Napping frequency',
        questionText: 'In a usual week, how many days do you take a daytime nap lasting at least 20 minutes?',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Always (7 days)' },
          { value: 2, label: '2 - Often (5-6 days)' },
          { value: 3, label: '3 - Sometimes (3-4 days)' },
          { value: 4, label: '4 - Rarely (1-2 days)' },
          { value: 5, label: '5 - Never (0 days)' },
        ],
        is_core_component: true, clinicalInterpretation: 'While short naps can be refreshing, frequent long naps may indicate underlying sleep issues.'
      },
      {
        key: 'daytime_sleepiness',
        name: 'Daytime sleepiness',
        questionText: 'On a typical day, how often do you feel drowsy or need a nap to get through your routine?',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Always (7 days)' },
          { value: 2, label: '2 - Often (5-6 days)' },
          { value: 3, label: '3 - Sometimes (3-4 days)' },
          { value: 4, label: '4 - Rarely (1-2 days)' },
          { value: 5, label: '5 - Never (0 days)' },
        ],
        is_core_component: true, clinicalInterpretation: 'Excessive daytime sleepiness can be a symptom of insufficient or poor-quality sleep.'
      },
      {
        key: 'sleep_disorders',
        questionText: 'In the past week, which of these sleep-related issues did you experience?',
        type: 'BooleanCheckbox',
        options: [
          { key: 'waking_drenched_sweats', label: 'Sleep disorder - Night sweats', is_core_component: true, clinicalInterpretation: 'Frequent night sweats can be a symptom of various conditions and should be discussed with a doctor.' },
          { key: 'teeth_grinding', label: 'Sleep disorder - Teeth grinding', is_core_component: true, clinicalInterpretation: 'Teeth grinding (bruxism) during sleep can lead to dental problems and jaw pain.' },
          { key: 'nasal_congestion_snoring', label: 'Sleep disorder - Breathing', is_core_component: true, clinicalInterpretation: 'Snoring or nasal congestion can disrupt sleep and may indicate conditions like sleep apnea.' },
          { key: 'restless_legs', label: 'Sleep disorder - Restless legs', is_core_component: true, clinicalInterpretation: 'Restless legs syndrome can make it difficult to fall asleep and stay asleep.' },
          { key: 'racing_thoughts', label: 'Sleep disorder - Racing thoughts', is_core_component: true, clinicalInterpretation: 'Difficulty quieting the mind at bedtime can be a sign of stress or anxiety affecting sleep.' },
          { key: 'upsetting_dreams', label: 'Sleep disorder - Nightmares', is_core_component: true, clinicalInterpretation: 'Frequent upsetting dreams or nightmares can impact sleep quality and emotional well-being.' },
        ],
        is_core_component: false, clinicalInterpretation: 'Various sleep-related issues can significantly affect health and recovery.' // Parent item
      },
    ],
  },
  {
    key: 'emotion',
    title: 'Emotion',
    parameters: [
      {
        key: 'little_interest',
        name: 'Little interest',
        questionText: 'Little interest or pleasure in doing things',
        type: 'Questionnaire',
        options: [
          { value: 0, label: '0 - Not at all' },
          { value: 1, label: '1 - Several days' },
          { value: 2, label: '2 - More than half the days' },
          { value: 3, label: '3 - Nearly every day' },
        ],
        is_core_component: true, clinicalInterpretation: 'Lack of interest or pleasure (anhedonia) is a key symptom of depression.'
      },
      {
        key: 'feeling_down',
        name: 'Feeling down',
        questionText: 'Feeling down, depressed, or hopeless',
        type: 'Questionnaire',
        options: [
          { value: 0, label: '0 - Not at all' },
          { value: 1, label: '1 - Several days' },
          { value: 2, label: '2 - More than half the days' },
          { value: 3, label: '3 - Nearly every day' },
        ],
        is_core_component: true, clinicalInterpretation: 'Persistent feelings of sadness or hopelessness are common in depression.'
      },
      {
        key: 'feeling_nervous',
        name: 'Feeling nervous',
        questionText: 'Feeling nervous, anxious, or on edge',
        type: 'Questionnaire',
        options: [
          { value: 0, label: '0 - Not at all' },
          { value: 1, label: '1 - Several days' },
          { value: 2, label: '2 - More than half the days' },
          { value: 3, label: '3 - Nearly every day' },
        ],
        is_core_component: true, clinicalInterpretation: 'Frequent nervousness or anxiety can significantly impact daily life.'
      },
      {
        key: 'not_being_able',
        name: 'Worrying',
        questionText: 'Not being able to stop or control worrying',
        type: 'Questionnaire',
        options: [
          { value: 0, label: '0 - Not at all' },
          { value: 1, label: '1 - Several days' },
          { value: 2, label: '2 - More than half the days' },
          { value: 3, label: '3 - Nearly every day' },
        ],
        is_core_component: true, clinicalInterpretation: 'Excessive and uncontrollable worry is a hallmark of anxiety disorders.'
      },
      {
        key: 'difficulty_concentrating',
        name: 'Difficulty concentrating',
        questionText: 'Difficulty concentrating in daily tasks',
        type: 'Questionnaire',
        options: [
          { value: 0, label: '0 - Not at all' },
          { value: 1, label: '1 - Several days' },
          { value: 2, label: '2 - More than half the days' },
          { value: 3, label: '3 - Nearly every day' },
        ],
        is_core_component: true, clinicalInterpretation: 'Concentration difficulties can be related to stress, anxiety, depression, or other conditions.'
      },
      {
        key: 'feeling_irritable',
        name: 'Feeling irritable',
        questionText: 'Feeling irritable and easily annoyed',
        type: 'Questionnaire',
        options: [
          { value: 0, label: '0 - Not at all' },
          { value: 1, label: '1 - Several days' },
          { value: 2, label: '2 - More than half the days' },
          { value: 3, label: '3 - Nearly every day' },
        ],
        is_core_component: true, clinicalInterpretation: 'Increased irritability can be a symptom of stress, mood disorders, or other issues.'
      },
      {
        key: 'felt_unable_control',
        name: 'Felt unable to control',
        questionText: 'Felt unable to control important things in your life',
        type: 'Questionnaire',
        options: [
          { value: 0, label: '0 - Never' },
          { value: 1, label: '1 - Almost never' },
          { value: 2, label: '2 - Sometimes' },
          { value: 3, label: '3 - Fairly often' },
          { value: 4, label: '4 - Very often' },
        ],
        is_core_component: true, clinicalInterpretation: 'Feeling a lack of control can contribute to stress and helplessness.'
      },
      {
        key: 'felt_confident_problems',
        name: 'Felt confident about problems (R)',
        questionText: 'Felt confident about your ability to handle personal problems',
        type: 'Questionnaire',
        options: [
          { value: 0, label: '0 - Never' },
          { value: 1, label: '1 - Almost never' },
          { value: 2, label: '2 - Sometimes' },
          { value: 3, label: '3 - Fairly often' },
          { value: 4, label: '4 - Very often' },
        ],
        is_core_component: true, clinicalInterpretation: 'Confidence in problem-solving is a sign of good coping skills and resilience. (R) indicates reversed scoring may apply.'
      },
      {
        key: 'felt_things_going_way',
        name: 'Felt things going way (R)',
        questionText: 'Felt that things were going your way',
        type: 'Questionnaire',
        options: [
          { value: 0, label: '0 - Never' },
          { value: 1, label: '1 - Almost never' },
          { value: 2, label: '2 - Sometimes' },
          { value: 3, label: '3 - Fairly often' },
          { value: 4, label: '4 - Very often' },
        ],
        is_core_component: true, clinicalInterpretation: 'A sense that things are going well is associated with positive well-being. (R) indicates reversed scoring may apply.'
      },
      {
        key: 'felt_difficulties_piling_up',
        name: 'Felt difficulties piling up',
        questionText: 'Felt difficulties were piling up so high that you could not overcome them',
        type: 'Questionnaire',
        options: [
          { value: 0, label: '0 - Never' },
          { value: 1, label: '1 - Almost never' },
          { value: 2, label: '2 - Sometimes' },
          { value: 3, label: '3 - Fairly often' },
          { value: 4, label: '4 - Very often' },
        ],
        is_core_component: true, clinicalInterpretation: 'Feeling overwhelmed by difficulties is a sign of significant stress.'
      },
      {
        key: 'lack_companionship',
        name: 'Lack companionship',
        questionText: 'I lack companionship.',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Hardly ever' },
          { value: 2, label: '2 - Some of the time' },
          { value: 3, label: '3 - Often' },
        ],
        is_core_component: true, clinicalInterpretation: 'Lack of companionship can contribute to feelings of loneliness.'
      },
      {
        key: 'feel_left_out',
        name: 'Feel left out',
        questionText: 'I feel left out.',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Hardly ever' },
          { value: 2, label: '2 - Some of the time' },
          { value: 3, label: '3 - Often' },
        ],
        is_core_component: true, clinicalInterpretation: 'Feeling left out can impact social well-being and mood.'
      },
      {
        key: 'feel_isolated',
        name: 'Feel isolated',
        questionText: 'I feel isolated from others.',
        type: 'Questionnaire',
        options: [
          { value: 1, label: '1 - Hardly ever' },
          { value: 2, label: '2 - Some of the time' },
          { value: 3, label: '3 - Often' },
        ],
        is_core_component: true, clinicalInterpretation: 'Social isolation is a risk factor for mental and physical health problems.'
      },
      {
        key: 'burnout_emotionally_exhausted',
        name: 'Burnout - Emotionally exhausted',
        questionText: 'I feel emotionally exhausted or drained most days.',
        type: 'BooleanCheckbox',
        is_core_component: true, clinicalInterpretation: 'Emotional exhaustion is a core symptom of burnout.'
      },
      {
        key: 'burnout_cynical',
        name: 'Burnout - Cynical',
        questionText: 'I have become cynical or detached from my work or tasks.',
        type: 'BooleanCheckbox',
        is_core_component: true, clinicalInterpretation: 'Cynicism or detachment can be a sign of burnout.'
      },
      {
        key: 'burnout_cant_disconnect',
        name: 'Burnout - Can\'t disconnect',
        questionText: 'I can\'t disconnect—my mind races about work even off-hours.',
        type: 'BooleanCheckbox',
        is_core_component: true, clinicalInterpretation: 'Inability to disconnect from work is a common feature of burnout.'
      },
      {
        key: 'burnout_no_breaks',
        name: 'Burnout - No breaks',
        questionText: 'I haven\'t taken a real break in weeks/months (no weekends or vacation).',
        type: 'BooleanCheckbox',
        is_core_component: true, clinicalInterpretation: 'Lack of adequate breaks and rest contributes to burnout.'
      },
      {
        key: 'burnout_long_hours',
        name: 'Burnout - Long hours',
        questionText: 'I work long hours without sufficient breaks during the day.',
        type: 'BooleanCheckbox',
        is_core_component: true, clinicalInterpretation: 'Consistently working long hours increases the risk of burnout.'
      },
      {
        key: 'burnout_personal_time',
        name: 'Burnout - Personal time',
        questionText: 'I lack time for myself or personal interests outside work.',
        type: 'BooleanCheckbox',
        is_core_component: true, clinicalInterpretation: 'Insufficient time for personal life is a factor in burnout.'
      },
      {
        key: 'burnout_family_time',
        name: 'Burnout - Family time',
        questionText: 'I lack time for family or friends because of work.',
        type: 'BooleanCheckbox',
        is_core_component: true, clinicalInterpretation: 'Work encroaching on family/social time can lead to burnout.'
      },
      {
        key: 'burnout_purpose',
        name: 'Burnout - Purpose (meaning)',
        questionText: 'I feel my work is no longer meaningful or "making a difference."',
        type: 'BooleanCheckbox',
        is_core_component: true, clinicalInterpretation: 'Loss of meaning or purpose in work is a significant aspect of burnout.'
      },
    ],
  },
  {
    key: 'environment',
    title: 'Environment',
    parameters: [
      {
        key: 'smoking_quantity_daily',
        name: 'Smoking Quantity (Daily)',
        questionText: 'On average, how many cigarettes or cigarette-equivalents do you smoke per day?',
        type: 'Questionnaire',
        options: [
          { value: 'none', label: 'None (I don’t smoke)' },
          { value: '1-5', label: '1–5 per day' },
          { value: '6-10', label: '6–10 per day' },
          { value: '11-20', label: '11–20 per day' },
          { value: 'gt20', label: 'More than 20 per day' },
        ],
        is_core_component: true,
        clinicalInterpretation: "Daily smoking quantity is a major risk factor for numerous health conditions."
      },
      {
        key: 'smoking_duration_years',
        name: 'Smoking Duration (Years)',
        questionText: 'For how many years have you been smoking (at any of those levels above)?',
        type: 'Questionnaire',
        options: [
          { value: 'never', label: 'Never' },
          { value: 'lt1', label: '< 1 year' },
          { value: '1-5', label: '1–5 years' },
          { value: '6-10', label: '6–10 years' },
          { value: 'gt10', label: '> 10 years' },
        ],
        is_core_component: true,
        clinicalInterpretation: "Duration of smoking significantly impacts cumulative health risks."
      },
      {
        key: 'outdoor_daylight_minutes',
        name: 'Outdoor Daylight Exposure (Daily)',
        questionText: 'On a typical day, how many minutes do you spend outside in natural daylight (not through a window)?',
        type: 'Questionnaire',
        options: [
          { value: '0', label: '0 min' },
          { value: '1-15', label: '1–15 min' },
          { value: '16-30', label: '16–30 min' },
          { value: '31-60', label: '31–60 min' },
          { value: 'gt60', label: '> 60 min' },
        ],
        is_core_component: true,
        clinicalInterpretation: "Exposure to natural daylight is important for circadian rhythm regulation and Vitamin D synthesis."
      },
      {
        key: 'nature_time_days_weekly',
        name: 'Time in Nature (Weekly)',
        questionText: 'In a usual week, how many days do you spend any time in a park, forest, beach, or garden?',
        type: 'Questionnaire',
        options: [
          { value: '0', label: '0 days' },
          { value: '1-2', label: '1–2 days' },
          { value: '3-4', label: '3–4 days' },
          { value: '5-6', label: '5–6 days' },
          { value: '7', label: '7 days' },
        ],
        is_core_component: true,
        clinicalInterpretation: "Spending time in natural environments has been linked to improved mental and physical well-being."
      },
      {
        key: 'morning_outdoor_light_days_weekly',
        name: 'Morning Outdoor Light (Weekly)',
        questionText: 'How often do you get at least 15 minutes of outdoor light before 10 AM?',
        type: 'Questionnaire',
        options: [
          { value: 'never', label: 'Never' },
          { value: 'rarely', label: 'Rarely (1–2 days/week)' },
          { value: 'sometimes', label: 'Sometimes (3–4 days/week)' },
          { value: 'often', label: 'Often (5–6 days/week)' },
          { value: 'always', label: 'Daily (7 days/week)' }, // Changed from 'daily' to 'always' to match activityDaysOptions pattern if intended
        ],
        is_core_component: true,
        clinicalInterpretation: "Morning light exposure helps anchor the circadian rhythm, which can improve sleep and mood."
      },
    ],
  },
];

// Helper function to get parameters for a specific lifestyle category
export const getParametersForLifestyleCategory = (categoryKey) => {
  const category = lifestyleCategories.find(cat => cat.key === categoryKey);
  return category ? category.parameters : [];
};

// Helper function to get all lifestyle parameters flattened
export const getAllLifestyleParameters = () => {
  return lifestyleCategories.reduce((acc, category) => {
    return acc.concat(category.parameters);
  }, []);
};
